import { Button, Checkbox, MenuItem, Modal, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import {
    StyledButtonsContainer,
    StyledCheckboxLabel,
    StyledCheckboxWrap,
    StyledContainer,
    StyledModalActions,
    StyledModalText,
} from "./styled-components";
import { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from "react";
import { Collaborator, UserSelection } from "@kaltura/mediaspace-shared-ui";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";

export interface SetOwnerResponse {
    /**
     * true if owner was set for **all** entries successfully, false otherwise
     */
    success: boolean;

    /**
     * list of entries where new owner could not be set
     */
    errorEntries?: { id: string; name: string }[];
}

export interface ChangeOwnerProps extends SharedMenuItemProps {
    setOwner: (userId: string, keepEditPermission: boolean) => Promise<SetOwnerResponse>;

    /**
     * a callback to the wrapper component indicating all is done
     */
    onOwnerSet?: (keepPermissions?: boolean) => void;

    /**
     * KMS action to call when searching for users.
     * the search term will be appended on the url.
     * expected response in the form of {screenName: string, id: string, thumbnailUrl?: string}[]
     */
    usersSearchUrl?: string;

    analyticsEventNameBase?: string;

    /**
     * close main menu
     */
    onCloseMenu: () => void;

    /**
     * text to show on the menuItem and modal title
     * default is "Transfer ownership"
     */
    actionText?: string;

    /**
     * pre-selected user to set as owner
     * when provided, the UserSelection input will not show
     */
    preSelectedUser?: Collaborator;
}

export function ChangeOwner({
    setOwner,
    onOwnerSet,
    usersSearchUrl,
    analyticsEventNameBase,
    onCloseMenu,
    autoFocus,
    actionText,
    preSelectedUser,
}: ChangeOwnerProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<Collaborator | undefined>(preSelectedUser);
    const [keepPermissions, setKeepPermissions] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const wasSubmitted = useRef(false);
    const keepPermissionsRef = useRef(keepPermissions);

    /**
     * call onOwnerSet after change owner request was submitted
     * done here instead of in the below handleSubmit to allow menu
     * and modal to close first before notifying the wrapper component
     */
    useEffect(() => {
        return () => {
            if (wasSubmitted.current) {
                // notify
                onOwnerSet?.(keepPermissionsRef.current);
            }
        };
    }, []);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const showOwnershipModal = () => {
        setIsModalOpen(true);
        if (analyticsEventNameBase != null) {
            sendButtonAnalytics(
                `${analyticsEventNameBase} - Transfer ownership selected`,
                ButtonClickAnalyticsType.CHOOSE
            );
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        if (analyticsEventNameBase != null) {
            sendButtonAnalytics(
                `${analyticsEventNameBase} - Transfer ownership canceled`,
                ButtonClickAnalyticsType.CHOOSE
            );
        }
        onCloseMenu();
    };

    const handleKeepPermissionsChanged = (e: ChangeEvent<HTMLInputElement>) => {
        setKeepPermissions(e.target.checked);
        const chk = e.target.checked ? "checked" : "unchecked";
        if (analyticsEventNameBase != null) {
            sendButtonAnalytics(
                `${analyticsEventNameBase} - Transfer ownership set collab - ${chk}`,
                ButtonClickAnalyticsType.CHOOSE
            );
        }
    };

    const handleSubmit = () => {
        if (analyticsEventNameBase != null) {
            sendButtonAnalytics(
                `${analyticsEventNameBase} - Transfer ownership saved`,
                ButtonClickAnalyticsType.CHOOSE
            );
        }
        setSubmitted(true);

        // selectedUser is defined when calling handleSubmit(), because the button is disabled when it's not defined
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setOwner(selectedUser!.id, keepPermissions)
            .then(
                (response) => {
                    if (response.success) {
                        showToast({ severity: "success", message: translate("Ownership successfully transferred") });
                    }
                    else if (response.errorEntries) {
                        let msg = translate("Could not update the following media:");
                        msg += "<ul>";
                        msg += response.errorEntries.map((e) => `<li>${e.id}, ${e.name}</li>`).join("");
                        showToast({ severity: "error", message: HtmlReactParser(msg) });
                    }
                    else {
                        showToast({ severity: "error", message: translate("Error updating media") });
                    }
                },
                () => {
                    showToast({ severity: "error", message: translate("Error updating media") });
                }
            )
            .finally(() => {
                wasSubmitted.current = true;
                keepPermissionsRef.current = keepPermissions;
                // close modal
                setIsModalOpen(false);
                onCloseMenu();
            });
    };

    return (
        <>
            <MenuItem onClick={() => showOwnershipModal()} autoFocus={autoFocus}>
                {actionText ?? translate("Transfer ownership")}
            </MenuItem>

            <Modal classes={{ paper: "kms-ds-set-ownership-modal" }} open={isModalOpen} fullWidth={true}>
                <ModalTitle>{actionText ?? translate("Transfer ownership")}</ModalTitle>
                <ModalContent>
                    <StyledContainer>
                        <StyledModalText
                            withUserSelection={!preSelectedUser}
                            variant={preSelectedUser ? "body1" : "body2"}
                        >
                            {translate(
                                "By changing ownership, you will no longer be able to perform any actions on the media or be able to see it in your My Media, unless you are a collaborator on the entry."
                            )}
                        </StyledModalText>
                        {!preSelectedUser && usersSearchUrl && (
                            <UserSelection
                                multiple={false}
                                placeholder={translate("Search for a user")}
                                aria-label={translate("Search for a user to set as owner")}
                                url={usersSearchUrl}
                                onChange={(event: SyntheticEvent<Element, Event>, value: Collaborator) => {
                                    setSelectedUser(value);
                                }}
                            />
                        )}
                    </StyledContainer>
                </ModalContent>
                <StyledModalActions>
                    <StyledCheckboxWrap>
                        <Checkbox id={"keepPermissions"} onChange={handleKeepPermissionsChanged} />
                        <StyledCheckboxLabel
                            text={translate("Make myself a collaborator")}
                            htmlFor={"keepPermissions"}
                        />
                    </StyledCheckboxWrap>
                    <StyledButtonsContainer>
                        <Button variant={"borderless"} color={"secondary"} onClick={() => handleCancel()}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            color={"danger"}
                            onClick={() => handleSubmit()}
                            loading={submitted}
                            disabled={!selectedUser || submitted}
                        >
                            {translate("Transfer ownership")}
                        </Button>
                    </StyledButtonsContainer>
                </StyledModalActions>
            </Modal>
        </>
    );
}

export default ChangeOwner;
